import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Button } from "@etframework/ui.components";
import useModalEvent from "../../hooks/useModalEvent";
import { ValidationProvider, useValidation } from "@etframework/ui.components";
import { RolloverTasksContent } from "../../components/RolloverTasksModalComponents/RolloverTasksContent";
import { RolloverTasksReview } from "../../components/RolloverTasksModalComponents/RolloverTasksReview";
import {
  useRolloverTasks,
  useTasksToRollover,
} from "../../data/mutations/taskMutations";
import { useBoardProvider } from "../../contexts/board/boardContext";
import { Loading } from "@etframework/ui.components";
import { TasksProcessing } from "../../components/RolloverTasksModalComponents/TasksProcessing";

export const RolloverTasksModal = () => {
  const { currentBoardId } = useBoardProvider();

  const { t } = useTranslation(["RolloverTasksModal"]);
  const { ModalEvents, useRegisterEvent } = useModalEvent();

  const rolloverTasksModalRef = useRef(null);
  const clientRef = useRef(null);
  const selectAllClientsCheckboxRef = useRef(null);
  const taskTypeRef = useRef(null);
  const dueDateYearContentRef = useRef(null);

  const [showModal, setShowModal] = useState(false);
  const [showProcessingTasks, setShowProcessingTasks] = useState(false);
  const [showButtons, setShowButtons] = useState(true);
  const [taskData, setTaskData] = useState([]);
  const [showFinalStep, setShowFinalStep] = useState(false);

  const { mutate: tasksToRollover, isPending: isPendingTasksToRollover } =
    useTasksToRollover();
  const { mutate: rolloverTasks, isPending: isPendingRolloverTasks } =
    useRolloverTasks();

  const closeModal = () => {
    setShowModal(false);

    setTimeout(() => {
      setShowFinalStep(false);
      setShowProcessingTasks(false);
      setShowButtons(true);
    }, 1000);
  };

  const openModal = () => {
    setShowModal(true);
  };

  const toggleModal = () => {
    if (showModal) {
      closeModal();
      return;
    } else {
      openModal();
    }
  };

  const ModalContent = () => {
    const { setValidationErrors } = useValidation();

    const onContinue = () => {
      if (dataIsValid()) {
        const data = {
          BoardId: currentBoardId,
          Clients: clientRef.current?.getComponentRef()?.getValue(),
          SelectAllClients: selectAllClientsCheckboxRef.current?.checked,
          TaskType: taskTypeRef.current?.getValue(),
          YearFrom: dueDateYearContentRef.current?.getFromYearValue(),
          YearTo: dueDateYearContentRef.current?.getToYearValue(),
        };

        tasksToRollover(
          { tasksToRollover: data },
          {
            onSuccess: (response) => {
              setShowFinalStep(true);
              setTaskData(response);
            },
            onError: (errors) => {
              setValidationErrors(errors?.data);
            },
          }
        );
      }
    };

    const onSubmit = () => {
      rolloverTasks(
        { tasks: taskData },
        {
          onSuccess: () => {
            setShowProcessingTasks(true);
            setShowButtons(false);
          },
          onError: () => {},
        }
      );
    };

    const handleRolloverTasksCheckboxChange = (e) => {
      if (e.checked) {
        clientRef?.current?.setIsReadonly(true);
        clientRef?.current?.getComponentRef()?.setValue(null);
      } else {
        clientRef?.current?.setIsReadonly(false);
      }
    };

    const handleTaskTypeChange = () => {
      taskTypeRef.current?.setShowError(false);

      const taskType = taskTypeRef.current?.getValue();

      if (!taskType) return;
      dueDateYearContentRef?.current.getTasksYears(taskType.Id);
    };

    const handleDueDateYearContentChange = () => {};

    const dataIsValid = () => {
      clientRef.current?.getComponentRef()?.setShowError(false);
      taskTypeRef.current?.setShowError(false);
      dueDateYearContentRef?.current
        ?.getFromYearComponent()
        ?.setShowError(false);
      dueDateYearContentRef?.current?.getToYearComponent()?.setShowError(false);

      const clientValue = clientRef.current?.getComponentRef()?.getValue();
      const isSelectAllClientsChecked =
        selectAllClientsCheckboxRef.current?.checked;
      var isClientValid =
        isSelectAllClientsChecked ||
        (clientValue != null && clientValue.length);

      if (!isClientValid) {
        clientRef.current?.getComponentRef()?.setShowError(true);
        clientRef.current?.getComponentRef()?.setError(t("Required"));
      }

      var taskTypeValue = taskTypeRef.current?.getValue();
      var isTaskTypeValid = taskTypeValue != null;

      if (!isTaskTypeValid) {
        taskTypeRef.current?.setShowError(true);
        taskTypeRef.current?.setError(t("Required"));
      }

      var fromYearValue = dueDateYearContentRef.current?.getFromYearValue();
      var toYearValue = dueDateYearContentRef.current?.getToYearValue();

      var isFromYearValid = fromYearValue != null;
      var isToYearValid = toYearValue != null;

      if (!isFromYearValid) {
        dueDateYearContentRef?.current
          ?.getFromYearComponent()
          ?.setShowError(true);
        dueDateYearContentRef?.current
          ?.getFromYearComponent()
          ?.setError(t("Required"));
      }

      if (!isToYearValid) {
        dueDateYearContentRef?.current
          ?.getToYearComponent()
          ?.setShowError(true);
        dueDateYearContentRef?.current
          ?.getToYearComponent()
          ?.setError(t("Required"));

        return isTaskTypeValid && isFromYearValid && isToYearValid;
      }

      return isTaskTypeValid;
    };

    return (
      <div className="rollover-tasks mx-3 my-1">
        {isPendingTasksToRollover || isPendingRolloverTasks ? (
          <div className="h-100">
            <Loading text={t("Loading")} />
          </div>
        ) : showProcessingTasks ? (
          <TasksProcessing
            onCloseClick={() => {
              closeModal();
            }}
          />
        ) : (
          <div>
            <div className="rollover-tasks-header">
              <div className="fw-bold fs-16">{t("RolloverTasksTitle")}</div>
              <div>
                {!showFinalStep
                  ? t("RolloverTasksSubtitle")
                  : t("RolloverTasksReview")}
              </div>
            </div>

            {!showFinalStep ? (
              <RolloverTasksContent
                clientRef={clientRef}
                selectAllClientsCheckboxRef={selectAllClientsCheckboxRef}
                taskTypeRef={taskTypeRef}
                dueDateYearContentRef={dueDateYearContentRef}
                onRolloverTasksCheckboxChange={
                  handleRolloverTasksCheckboxChange
                }
                onTaskTypeChange={handleTaskTypeChange}
                onDueDateYearContentChange={handleDueDateYearContentChange}
              />
            ) : (
              <RolloverTasksReview taskData={taskData} />
            )}
          </div>
        )}

        {showButtons && (
          <div className="rollover-tasks-footer d-flex">
            <div className="col d-flex justify-content-end rollover-tasks-btns gap-3">
              <Button
                id={`${ModalEvents.RolloverTasks.Id + "_close-button"}`}
                text={t("Cancel")}
                className="btn-tertiary w-auto"
                onClick={closeModal}
              />
              <Button
                id={`${ModalEvents.RolloverTasks.Id + "_save-changes-button"}`}
                text={!showFinalStep ? t("Continue") : t("ConfirmRollover")}
                className="btn btn-primary w-auto"
                isDisabled={showFinalStep && taskData?.TaskCount == 0}
                onClick={!showFinalStep ? onContinue : onSubmit}
                loading={isPendingTasksToRollover || isPendingRolloverTasks}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  useRegisterEvent(ModalEvents.RolloverTasks, toggleModal);

  return (
    <ValidationProvider>
      <Modal
        id={ModalEvents.RolloverTasks.Id}
        ref={rolloverTasksModalRef}
        isModal
        useSpinner
        showCloseIcon={true}
        visible={showModal}
        content={<ModalContent />}
        close={closeModal}
        minHeight="20vh"
        width="500px"
      ></Modal>
    </ValidationProvider>
  );
};
